import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, TextInput, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import InlineMessage from '../../src/components/InlineMessage';
import { useTheme } from '../../src/context/ThemeContext';
import { useResponsive } from '../../src/utils/responsive';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';

interface FeedbackItem {
  id: string; userId: string; role: string; type: string; message: string;
  rating: number | null; status: string; adminNotes: string | null; createdAt: string;
}

const TYPE_FILTERS = [
  { label: 'Bug', value: 'bug' },
  { label: 'Feature', value: 'feature' },
  { label: 'Complaint', value: 'complaint' },
  { label: 'Praise', value: 'praise' },
];

const STATUS_OPTIONS = ['new', 'in_progress', 'resolved', 'dismissed'];

const typeColor = (t: string) => {
  if (t === 'bug') return '#dc2626';
  if (t === 'feature') return '#2563eb';
  if (t === 'complaint') return '#d97706';
  if (t === 'praise') return '#059669';
  return '#6b7280';
};

const statusColor = (s: string) => {
  if (s === 'new') return '#2563eb';
  if (s === 'in_progress') return '#d97706';
  if (s === 'resolved') return '#059669';
  return '#6b7280';
};

/** AdminFeedback - review, filter, and respond to user-submitted feedback and bug reports. */
export default function AdminFeedback() {
  const { colors } = useTheme();
  const { width, height } = useResponsive();
  const isMobile = width < 768;
  const styles = useStyles(colors, isMobile);
  const modalBottomPadding = useModalBottomPadding();
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [modalItem, setModalItem] = useState<FeedbackItem | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const fetchData = useCallback(async (p = 1, s = '', type = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (s) params.set('search', s);
      if (type) params.set('type', type);
      const data = await api<{ feedback: FeedbackItem[]; total: number }>(`/api/admin/feedback?${params}`);
      if (signal?.aborted) return;
      setItems(data.feedback);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', '', ac.signal); return () => ac.abort(); }, []);

  const openModal = (item: FeedbackItem) => {
    setModalItem(item);
    setEditStatus(item.status);
    setEditNotes(item.adminNotes || '');
    setErrorMsg('');
  };

  const saveNotes = async () => {
    if (!modalItem) return;
    setSaving(true);
    setErrorMsg('');
    try {
      await api(`/api/admin/feedback/${modalItem.id}`, {
        method: 'PUT',
        body: { status: editStatus, adminNotes: editNotes },
      });
      setModalItem(null);
      fetchData(page, search, typeFilter);
    } catch (err: any) {
      setErrorMsg(err.message);
    } finally { setSaving(false); }
  };

  const csvEndpoint = `/api/admin/feedback?${new URLSearchParams({ ...(search && { search }), ...(typeFilter && { type: typeFilter }) })}`;

  const renderItem = ({ item }: { item: FeedbackItem }) => (
    <TouchableOpacity style={styles.row} onPress={() => openModal(item)}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <View style={[styles.badge, { backgroundColor: typeColor(item.type) + '15' }]}>
            <Text style={[styles.badgeText, { color: typeColor(item.type) }]}>{item.type}</Text>
          </View>
          <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '15' }]}>
            <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status.replace('_', ' ')}</Text>
          </View>
        </View>
        <Text style={styles.message} numberOfLines={2}>{item.message}</Text>
        <Text style={styles.meta}>
          {item.role} · {item.rating != null ? `Rating: ${item.rating}/5 · ` : ''}
          {new Date(item.createdAt).toLocaleString()}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color={colors.textLight} />
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search, typeFilter)}
        total={total} page={page} limit={20} onPageChange={(p) => fetchData(p, search, typeFilter)}
        csvEndpoint={csvEndpoint} csvFilename="feedback.csv" label="feedback item"
        filterOptions={TYPE_FILTERS} activeFilter={typeFilter}
        onFilterChange={(v) => { setTypeFilter(v); fetchData(1, search, v); }}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={items} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No feedback found</Text>}
        />
      )}

      <Modal visible={!!modalItem} transparent animationType="slide" onRequestClose={() => setModalItem(null)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>Feedback Detail</Text>
            <ScrollView style={{ maxHeight: isMobile ? height * 0.6 : 400 }} nestedScrollEnabled>
              <Text style={styles.modalLabel}>Type</Text>
              <Text style={styles.modalValue}>{modalItem?.type}</Text>
              <Text style={styles.modalLabel}>Message</Text>
              <Text style={styles.modalValue}>{modalItem?.message}</Text>
              {modalItem?.rating != null && (
                <><Text style={styles.modalLabel}>Rating</Text><Text style={styles.modalValue}>{modalItem.rating}/5</Text></>
              )}
              <Text style={styles.modalLabel}>Status</Text>
              <View style={styles.statusRow}>
                {STATUS_OPTIONS.map(s => (
                  <TouchableOpacity key={s} onPress={() => setEditStatus(s)}
                    style={[styles.statusChip, editStatus === s && styles.statusChipActive]}>
                    <Text style={[styles.statusChipText, editStatus === s && styles.statusChipTextActive]}>
                      {s.replace('_', ' ')}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
              <Text style={styles.modalLabel}>Admin Notes</Text>
              <TextInput
                style={styles.notesInput}
                placeholder="Add notes..."
                placeholderTextColor={colors.textLight}
                value={editNotes}
                onChangeText={setEditNotes}
                multiline
                numberOfLines={3}
                maxLength={500}
              />
            </ScrollView>
            <InlineMessage type="error" message={errorMsg} visible={!!errorMsg} onDismiss={() => setErrorMsg('')} />
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.cancelBtn} onPress={() => setModalItem(null)}>
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.saveBtn} onPress={saveNotes} disabled={saving}>
                <Text style={styles.saveText}>{saving ? 'Saving...' : 'Save'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme, isMobile: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: 8 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', gap: 8, marginBottom: 4 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, textTransform: 'capitalize' },
  message: { fontSize: fontSize.sm, color: c.text, marginBottom: 2 },
  meta: { fontSize: fontSize.xs, color: c.textLight },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: isMobile ? 'flex-end' : 'center', alignItems: 'center', padding: isMobile ? 0 : spacing.lg },
  modalContent: { backgroundColor: c.surface, borderRadius: isMobile ? 0 : borderRadius.lg, borderTopLeftRadius: borderRadius.lg, borderTopRightRadius: borderRadius.lg, padding: spacing.lg, width: '100%', maxWidth: isMobile ? undefined : 500 },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  modalLabel: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, marginTop: spacing.sm, textTransform: 'uppercase' },
  modalValue: { fontSize: fontSize.sm, color: c.text, marginTop: 2 },
  statusRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs },
  statusChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  statusChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  statusChipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium, textTransform: 'capitalize' },
  statusChipTextActive: { color: c.white },
  notesInput: {
    backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm,
    fontSize: fontSize.sm, color: c.text, marginTop: spacing.xs, minHeight: 80, textAlignVertical: 'top',
    borderWidth: 1, borderColor: c.border,
  },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.md },
  cancelBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm },
  cancelText: { fontSize: fontSize.sm, color: c.textSecondary, fontWeight: fontWeight.medium },
  saveBtn: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.sm, backgroundColor: c.primary },
  saveText: { fontSize: fontSize.sm, color: c.white, fontWeight: fontWeight.semibold },
});
const useStyles = (c: ColorTheme, isMobile: boolean) => React.useMemo(() => createStyles(c, isMobile), [c, isMobile]);
